import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, EnvelopeSimple, ChatCircle, PaperPlaneTilt, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Notifications = () => {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState({ sms: false, email: false, jobs: [] });
  const [smsForm, setSmsForm] = useState({ to: '', body: '' });
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const [balanceThreshold, setBalanceThreshold] = useState(0);
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({ channel: '', status: '' });

  useEffect(() => {
    notificationsApi.status().then(({ data }) => setStatus(data)).catch(() => {});
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const params = {};
      if (logFilters.channel) params.channel = logFilters.channel;
      if (logFilters.status) params.status = logFilters.status;
      const { data } = await notificationsApi.getLogs(params);
      setLogs(data || []);
    } catch {
      /* ignore */
    }
  };

  const sendTest = async (channel, payload) => {
    try {
      const { data } = await notificationsApi.test({ channel, ...payload });
      if (data.sent) toast.success(t('success'));
      else toast.error(data.error || t('error'));
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    }
  };

  const triggerAppointmentReminders = async (daysAhead = 1) => {
    try {
      const { data } = await notificationsApi.remindAppointments(daysAhead);
      setResults({ kind: 'appointments', data });
      toast.success(`${data.sent_sms + data.sent_email} ${language === 'fr' ? 'rappels envoyés' : 'reminders sent'} (J-${daysAhead})`);
      loadLogs();
    } catch {
      toast.error(t('error'));
    }
  };

  const triggerBalanceReminders = async () => {
    try {
      const { data } = await notificationsApi.remindBalances({ threshold: Number(balanceThreshold) || 0 });
      setResults({ kind: 'balances', data });
      toast.success(`${data.sent_email} ${language === 'fr' ? 'emails envoyés' : 'emails sent'}`);
      loadLogs();
    } catch {
      toast.error(t('error'));
    }
  };

  const StatusBadge = ({ active, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {active ? <Check size={12} /> : <X size={12} />} {label}
    </span>
  );

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center gap-3">
        <Bell size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('notifications')}</h1>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader><CardTitle className="font-outfit text-lg">{language === 'fr' ? 'État des canaux' : 'Channel status'}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-center">
          <StatusBadge active={status.sms} label={`SMS (Twilio): ${status.sms ? (language === 'fr' ? 'configuré' : 'configured') : (language === 'fr' ? 'non configuré' : 'not configured')}`} />
          <StatusBadge active={status.email} label={`Email (Resend): ${status.email ? (language === 'fr' ? 'configuré' : 'configured') : (language === 'fr' ? 'non configuré' : 'not configured')}`} />
          {(!status.sms || !status.email) && (
            <p className="text-xs text-[#64748B] w-full mt-2">
              {language === 'fr'
                ? 'Configurez TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER, RESEND_API_KEY dans backend/.env puis redémarrez le backend.'
                : 'Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER, RESEND_API_KEY in backend/.env and restart backend.'}
            </p>
          )}
          {(status.jobs || []).length > 0 && (
            <div className="w-full mt-3 pt-3 border-t border-[#E2E8F0]">
              <p className="text-xs font-semibold text-[#0F172A] mb-2">{language === 'fr' ? 'Tâches programmées' : 'Scheduled jobs'}</p>
              <div className="space-y-1">
                {status.jobs.map(j => (
                  <div key={j.id} className="text-xs text-[#64748B] flex items-center gap-2" data-testid={`cron-job-${j.id}`}>
                    <span className="px-2 py-0.5 rounded bg-[#F1F5F9] font-mono">{j.id}</span>
                    <span>{j.trigger}</span>
                    <span>→ {language === 'fr' ? 'prochain' : 'next'}: {j.next_run_time ? new Date(j.next_run_time).toLocaleString() : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="reminders">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="reminders" data-testid="tab-reminders">{language === 'fr' ? 'Rappels' : 'Reminders'}</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test">{language === 'fr' ? 'Tester un envoi' : 'Test send'}</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">{language === 'fr' ? 'Journal' : 'Logs'}</TabsTrigger>
        </TabsList>

        <TabsContent value="reminders" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit">{language === 'fr' ? 'Rappels rendez-vous' : 'Appointment reminders'}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-[#64748B]">
                {language === 'fr'
                  ? "Envoie SMS + Email aux parents. Idempotent (chaque RDV n'est rappelé qu'une fois par horizon)."
                  : 'Sends SMS + Email to parents. Idempotent (each appt reminded once per horizon).'}
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => triggerAppointmentReminders(1)} data-testid="trigger-appt-reminders" className="bg-[#0052FF] hover:bg-[#0040D9]">
                  <PaperPlaneTilt size={18} className="mr-2" />{language === 'fr' ? 'J-1 maintenant' : 'T-1 now'}
                </Button>
                <Button onClick={() => triggerAppointmentReminders(14)} variant="outline" data-testid="trigger-appt-reminders-d14">
                  <PaperPlaneTilt size={18} className="mr-2" />{language === 'fr' ? 'J-14 maintenant' : 'T-14 now'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit">{language === 'fr' ? 'Rappels de solde impayé' : 'Unpaid balance reminders'}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-[#64748B]">
                {language === 'fr'
                  ? 'Envoie un email aux parents dont l\'enfant a un solde restant supérieur ou égal au seuil.'
                  : 'Sends an email to parents whose child has a remaining balance >= threshold.'}
              </p>
              <div className="flex items-end gap-3">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Seuil (FCFA)' : 'Threshold (FCFA)'}</Label>
                  <Input type="number" data-testid="balance-threshold" value={balanceThreshold} onChange={(e) => setBalanceThreshold(e.target.value)} className="w-40" />
                </div>
                <Button onClick={triggerBalanceReminders} data-testid="trigger-balance-reminders" className="bg-[#0052FF] hover:bg-[#0040D9]">
                  <PaperPlaneTilt size={18} className="mr-2" />{language === 'fr' ? 'Envoyer les rappels' : 'Send reminders'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {results && (
            <Card className="border border-[#E2E8F0] shadow-none">
              <CardHeader><CardTitle className="font-outfit text-base">{language === 'fr' ? 'Dernier résultat' : 'Last result'}</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-xs bg-[#F8FAFC] p-3 rounded overflow-auto" data-testid="reminders-result">{JSON.stringify(results.data, null, 2)}</pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="test" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit text-lg flex items-center gap-2"><ChatCircle size={20} />SMS</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Numéro (E.164)' : 'Number (E.164)'}</Label>
                <Input data-testid="test-sms-to" placeholder="+221771234567" value={smsForm.to} onChange={(e) => setSmsForm({ ...smsForm, to: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Message' : 'Body'}</Label>
                <Textarea data-testid="test-sms-body" rows={3} value={smsForm.body} onChange={(e) => setSmsForm({ ...smsForm, body: e.target.value })} />
              </div>
              <Button onClick={() => sendTest('sms', smsForm)} className="bg-[#0052FF] hover:bg-[#0040D9]" data-testid="send-test-sms" disabled={!status.sms}>
                <PaperPlaneTilt size={18} className="mr-2" />{language === 'fr' ? 'Envoyer SMS' : 'Send SMS'}
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit text-lg flex items-center gap-2"><EnvelopeSimple size={20} />Email</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Destinataire' : 'To'}</Label>
                <Input data-testid="test-email-to" type="email" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Sujet' : 'Subject'}</Label>
                <Input data-testid="test-email-subject" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Message' : 'Body'}</Label>
                <Textarea data-testid="test-email-body" rows={3} value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} />
              </div>
              <Button onClick={() => sendTest('email', emailForm)} className="bg-[#0052FF] hover:bg-[#0040D9]" data-testid="send-test-email" disabled={!status.email}>
                <PaperPlaneTilt size={18} className="mr-2" />{language === 'fr' ? 'Envoyer Email' : 'Send Email'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">{language === 'fr' ? 'Journal des envois' : 'Send logs'}</CardTitle>
              <div className="flex gap-2 items-center">
                <select
                  data-testid="log-filter-channel"
                  className="text-sm border border-[#E2E8F0] rounded px-2 py-1"
                  value={logFilters.channel}
                  onChange={(e) => setLogFilters({ ...logFilters, channel: e.target.value })}
                >
                  <option value="">{language === 'fr' ? 'Tous canaux' : 'All channels'}</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
                <select
                  data-testid="log-filter-status"
                  className="text-sm border border-[#E2E8F0] rounded px-2 py-1"
                  value={logFilters.status}
                  onChange={(e) => setLogFilters({ ...logFilters, status: e.target.value })}
                >
                  <option value="">{language === 'fr' ? 'Tous statuts' : 'All statuses'}</option>
                  <option value="sent">{language === 'fr' ? 'Envoyé' : 'Sent'}</option>
                  <option value="failed">{language === 'fr' ? 'Échec' : 'Failed'}</option>
                  <option value="disabled">{language === 'fr' ? 'Désactivé' : 'Disabled'}</option>
                </select>
                <Button size="sm" onClick={loadLogs} variant="outline" data-testid="refresh-logs-btn">
                  {language === 'fr' ? 'Actualiser' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-[#64748B] py-6 text-center">{t('noData')}</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-auto">
                  {logs.map((l) => (
                    <div key={l.id} className="p-3 border border-[#E2E8F0] rounded-lg text-sm flex items-start gap-3" data-testid={`log-row-${l.id}`}>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                        l.status === 'sent' ? 'bg-green-100 text-green-700' :
                        l.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{l.channel.toUpperCase()} · {l.status}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{l.to}</span>
                          <span className="text-xs text-[#64748B] whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</span>
                        </div>
                        {l.subject && <p className="text-xs text-[#0F172A] font-medium">{l.subject}</p>}
                        <p className="text-xs text-[#64748B] truncate">{l.body_preview}</p>
                        {l.error && <p className="text-xs text-red-600 mt-1">⚠ {l.error}</p>}
                        {l.provider_id && <p className="text-xs text-[#94A3B8] mt-1 font-mono">{l.provider_id}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
